import { HelpCircle } from 'lucide-react';

const COPY = {
  readiness: {
    title: 'What this is',
    lead: 'One score for how hirable you look right now, not how you feel.',
    points: [
      ['Academic', 'Your education and credentials on record.'],
      ['Skills', 'What you can actually do, and how much of it is proven.'],
      ['Experience', 'Work, projects and placements that back up the claims.'],
      ['Professional Presence', 'How you look when someone searches your name, mainly LinkedIn.'],
      ['Network', 'Who knows you well enough to vouch for you or hand you a lead.'],
      ['Job Search', 'Whether you have a named target and are actually moving on it.'],
    ],
  },
  position: {
    title: 'What "brand" and "visibility" actually mean',
    lead: 'This is your LinkedIn profile and anywhere else a stranger looks you up, a recruiter, a hiring manager, an alum you just messaged, a client. Nine times in ten they check before they reply, and they decide in about six seconds whether to keep reading.',
    points: [
      ['Brand pillars', 'The three things you want to be known for. Not adjectives, capabilities. "Risk and compliance", "security analysis", "training teams". They decide what your headline and About section say, and they keep you from sounding like everyone else.'],
      ['Headline', 'The one line under your name, and the single most-read sentence you own. It should name the work you want next, not only the job or degree you have now. "Business Analytics senior at NC A&T, turning messy data into decisions people act on" does work that "Student" never will.'],
      ['About section', 'The short story of where you have been and where you are going, written around your pillars. This is what turns a career change, a service record or a gap in employment into intent rather than a question mark.'],
      ['Your LinkedIn URL', 'Paste it and we score what is actually live, not what you meant to publish. It also becomes the link you put on every application, résumé and email signature.'],
      ['Visibility score', 'How findable and convincing you are to someone who does not know you: headline, About, at least one piece of public evidence, and real relationships in the field. Each part names the points it is worth.'],
      ['Digital professionalism', 'The eight habits that decide whether people reply at all, never sending a blank connection request, following up once and then letting it go, asking for a conversation instead of a job, thanking people and reporting back on advice you used.'],
      ['Other platforms', 'LinkedIn is the one that matters most, but not the only one. Students are searched on Handshake, technical and creative work lives on GitHub, Behance or Dribbble, founders and consultants need an address they own, trades and creators genuinely get found on Instagram, TikTok and YouTube, and everyone should search their own name in a private window to see what actually comes up first.'],
      ['What we never do', 'FuturePath never posts, never messages anyone and never logs into an account for you. Everything is written with you and copied by you.'],
    ],
  },
  connect: {
    title: 'Why this page exists',
    lead: 'Most roles are filled through people. This page is about building the nine kinds of relationship that make a move possible, and finding those people on LinkedIn, in associations and in alumni networks.',
    points: [
      ['Board of advisors', 'Nine roles, not nine friends. A transition mentor, an industry mentor, a connector, a recruiter relationship, a sponsor, and so on. Each does a different job for you, and one person can only cover one of them well.'],
      ['Click any name', 'Every person on your board opens up: the role they play and why it matters, how you met, the advice they gave you, your notes and when you last spoke. From there you can edit their details or draft the next message to them.'],
      ['Network readiness', 'How many of those roles are actually filled. Empty roles are the ones nobody is advocating for you in.'],
      ['Find a mentor', 'Say what kind of person you need and where you are. We tell you exactly where they concentrate and hand you a pre-built LinkedIn search.'],
      ['Advice received', 'Log what someone told you. Going back later to say "I did the thing you suggested, here is what happened" is the single move that turns a contact into a mentor, and almost nobody does it.'],
      ['Outreach drafting', 'We write the first message from what the platform already knows about you, in the right format, and score it before you send it. You always send it yourself.'],
    ],
  },
  radar: {
    title: 'What this page does',
    lead: 'It watches your situation and the market so you don\'t have to check. Opportunities we found for you, risk that\'s building, and a recalculation whenever your life changes.',
    points: [
      ['Opportunities', 'Real, named programs, scholarships and funding matched to your profile and where you live.'],
      ['Early warning', 'A risk band with named drivers, high AI exposure in your field, a shrinking role, a thin skill record, no relationships.'],
      ['Signals', 'Each one says why it became relevant now, and links to the one screen that clears it.'],
    ],
  },
};

export const ModuleExplainer = ({ id, testId }) => {
  const c = COPY[id];
  if (!c) return null;
  return (
    <details className="mb-6 group" data-testid={testId || `explainer-${id}`}>
      <summary className="fp-pill cursor-pointer list-none inline-flex" data-testid={`explainer-toggle-${id}`}>
        <HelpCircle className="w-3.5 h-3.5 text-cyan-300" />
        {c.title}
        <span className="text-slate-300 group-open:hidden">- read this first</span>
      </summary>
      <div className="mt-3 fp-rail relative overflow-hidden rounded-xl border border-slate-700/70 bg-slate-900/50 p-5">
        <p className="text-sm text-slate-200">{c.lead}</p>
        <dl className="mt-4 space-y-2.5">
          {c.points.map(([term, def]) => (
            <div key={term} className="flex flex-col sm:flex-row sm:gap-3">
              <dt className="text-sm font-semibold text-white sm:w-52 shrink-0">{term}</dt>
              <dd className="text-sm text-slate-300/90">{def}</dd>
            </div>
          ))}
        </dl>
      </div>
    </details>
  );
};

export default ModuleExplainer;
